import React, { useEffect, useState } from 'react'

import { toast, ToastContainer } from 'react-toastify'
import { MdUpload } from 'react-icons/md';
import { aboutUsDetailsApi } from 'Services/allApi';
import { aboutDetailsEditApi } from 'Services/allApi';
import AboutContent from './AboutContent';
import WhyLuminar from './WhyLuminar';
import CeoMessage from './CeoMessage';
import MissionVision from './MissionVision';


const AboutUs = () => {
    const [formData, setFormData] = useState({
        identifier: '',
        page_url: '',
        title: '',
        meta_description: '',
        meta_keywords: '',
        meta_robots: '',
        canonical_url: '',
        og_title: '',
        og_description: '',
        og_url: '',
        og_site_name: '',
        og_locale: '',
        owner: '',
        custom_meta_tags: {},
    });

    const [og_image, setOg_image] = useState(null);
    const [og_image_preview, setOg_image_preview] = useState(null); // To store the preview URL of the image
    const [loading, setLoading] = useState(false); // Loader state

    const [customMetaTagsText, setCustomMetaTagsText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false); // To track submission state

    useEffect(() => {
        const fetchDetails = async () => {
            setLoading(true); // Show loader
            try {
                const response = await aboutUsDetailsApi();
                if (response.error) {
                    throw new Error(response.message);
                }
                const data = response.data;
                setFormData({
                    identifier: data.identifier || '',
                    page_url: data.page_url || '',
                    title: data.title || '',
                    meta_description: data.meta_description || '',
                    meta_keywords: data.meta_keywords || '',
                    meta_robots: data.meta_robots || '',
                    canonical_url: data.canonical_url || '',
                    og_title: data.og_title || '',
                    og_description: data.og_description || '',
                    og_url: data.og_url || '',
                    og_site_name: data.og_site_name || '',
                    og_locale: data.og_locale || '',
                    owner: data.owner || '',
                    custom_meta_tags: data.custom_meta_tags || {},
                });

                // Convert custom_meta_tags object to text format for display
                const metaTagsText = Object.entries(data.custom_meta_tags || {})
                    .map(([key, value]) => `${key}=${value}`)
                    .join('\n');
                setCustomMetaTagsText(metaTagsText);

                if (data.og_image) {
                    if (data.og_image instanceof Blob || data.og_image instanceof File) {
                        setOg_image_preview(URL.createObjectURL(data.og_image));
                    } else {
                        setOg_image_preview(data.og_image);
                    }
                } else {
                    setOg_image_preview(null);
                }
            } catch (error) {
                toast.error('Failed to load homepage details: ' + error.message);
            } finally {
                setLoading(false); // Hide loader
            }
        };

        fetchDetails();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCustomMetaTagsChange = (e) => {
        setCustomMetaTagsText(e.target.value);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0]; // Get the uploaded file
        if (file) {
            setOg_image(file); // Store the image file in the state
            setOg_image_preview(URL.createObjectURL(file)); // Create a preview URL
        }
    };

    const validateUrl = (url) => {
        const urlPattern = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z0-9]{2,6}(\/[a-z0-9-.~%])(\?[a-z0-9-.~%&=]*)?$/i;
        return urlPattern.test(url) && !url.includes(' ');
    };

    const handleUpdate = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);

        // Prepare custom meta tags
        const customMetaTags = customMetaTagsText.split('\n').reduce((acc, line) => {
            const [key, value] = line.split('=');
            if (key && value) {
                acc[key.trim()] = value.trim();
            }
            return acc;
        }, {});

        // Validate form data
        if (!formData.page_url) {
            toast.error('Page URL is required!');
            setIsSubmitting(false);
            return;
        }

        // Prepare the final data to send
        const formDataToSend = new FormData();
        const dataToSend = { ...formData, custom_meta_tags: customMetaTags };

        // Append form data values
        for (let key in dataToSend) {
            if (key === 'custom_meta_tags') {
                formDataToSend.append(key, JSON.stringify(dataToSend[key]));
            } else {
                formDataToSend.append(key, dataToSend[key]);
            }
        }

        if (og_image) {
            formDataToSend.append('og_image', og_image);
        }

        // Log form data for debugging
        for (let pair of formDataToSend.entries()) {
            console.log(pair[0] + ': ' + pair[1]);
        }

        try {
            const response = await aboutDetailsEditApi(formData.identifier, formDataToSend);
            if (response.error) {
                toast.error('Failed to update homepage details: ' + response.message);
            } else {
                toast.success('Homepage details updated successfully!');
            }
        } catch (error) {
            toast.error('Error updating homepage details: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    return (
        <>
            <div>
                <div>
                    {loading ? (
                        <div className="text-center text-lg font-bold text-gray-700">Loading...</div>
                    ) : (
                        <div className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-lg mt-3  dark:!bg-navy-700 dark:text-white">
                            <h2 className="text-center text-3xl font-bold mb-5 mt-2">About SEO</h2>
                            <hr className="mb-4" />
                            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-4 mt-5">
                                {/* Page URL */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="pageUrl" className="block font-medium text-gray-900">Page URL</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="page_url"
                                            id="pageUrl"
                                            required
                                            value={formData.page_url}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>
                                {/* Title */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="title" className="block font-medium text-gray-900">Title</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="title"
                                            id="title"
                                            required
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* Meta Description */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="metaDescription" className="block font-medium text-gray-900">Meta Description</label>
                                    <div className="mt-2">
                                        <textarea
                                            name="meta_description"
                                            id="metaDescription"
                                            required
                                            value={formData.meta_description}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* Meta Keywords */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="metaKeywords" className="block font-medium text-gray-900">Meta Keywords</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="meta_keywords"
                                            id="metaKeywords"
                                            required
                                            value={formData.meta_keywords}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* Meta Robots */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="metaRobots" className="block font-medium text-gray-900">Meta Robots</label>
                                    <div className="mt-2">
                                        <select
                                            name="meta_robots"
                                            id="metaRobots"
                                            required
                                            value={formData.meta_robots}
                                            onChange={handleInputChange}
                                            className="block w-full h-9 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        >
                                            <option value="">Select an option</option>
                                            <option value="index, follow">Index, Follow</option>
                                            <option value="noindex, nofollow">Noindex, Nofollow</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Canonical URL */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="canonicalUrl" className="block font-medium text-gray-900">Canonical URL</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="canonical_url"
                                            id="canonicalUrl"
                                            required
                                            value={formData.canonical_url}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* OG Title */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="ogTitle" className="block font-medium text-gray-900">OG Title</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="og_title"
                                            id="ogTitle"
                                            required
                                            value={formData.og_title}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* OG Description */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="ogDescription" className="block font-medium text-gray-900">OG Description</label>
                                    <div className="mt-2">
                                        <textarea
                                            type="text"
                                            name="og_description"
                                            id="ogDescription"
                                            required
                                            value={formData.og_description}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* OG URL */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="ogUrl" className="block font-medium text-gray-900">OG URL</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="og_url"
                                            id="ogUrl"
                                            required
                                            value={formData.og_url}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* OG Site Name */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="ogSiteName" className="block font-medium text-gray-900">OG Site Name</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="og_site_name"
                                            id="ogSiteName"
                                            required
                                            value={formData.og_site_name}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* OG Locale */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="ogLocale" className="block font-medium text-gray-900">OG Locale</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="og_locale"
                                            id="ogLocale"
                                            required
                                            value={formData.og_locale}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* Owner */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="owner" className="block font-medium text-gray-900">Owner</label>
                                    <div className="mt-2">
                                        <input
                                            type="text"
                                            name="owner"
                                            id="owner"
                                            required
                                            value={formData.owner}
                                            onChange={handleInputChange}
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* Custom Meta Tags */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="customMetaTags" className="block font-medium text-gray-900">Custom Meta Tags</label>
                                    <div className="mt-2">
                                        <textarea
                                            name="customMetaTags"
                                            id="customMetaTags"
                                            value={customMetaTagsText}
                                            onChange={handleCustomMetaTagsChange}
                                            rows="8"
                                            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-purple-800 sm:text-sm/6  dark:!bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* OG Image */}
                                <div className="sm:col-span-2 flex flex-col sm:flex-row items-center sm:justify-between mt-4">
                                    <div className="sm:w-1/2 flex justify-center mb-4 sm:mb-0">
                                        {og_image_preview && (
                                            <div className="max-w-full max-h-64">
                                                <img
                                                    src={og_image_preview} // Corrected to use og_image_preview
                                                    alt="OG Image Preview"
                                                    className="w-full rounded shadow-md"
                                                    style={{ height: '130px' }}
                                                />
                                            </div>
                                        )}
                                        <div className="sm:w-1/2 text-center">
                                            <label htmlFor="og_image" className="block font-medium text-gray-900 mb-2">OG Image</label>
                                            <input
                                                type="file"
                                                id="og_image"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={handleImageUpload}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => document.getElementById('og_image').click()}
                                                className="px-4 py-2 bg-purple-600 text-white rounded-md shadow hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            >
                                                <MdUpload />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Update Button */}
                                <div className="sm:col-span-4 text-center">
                                    <hr className="mt-4 mb-4" />
                                    <button
                                        type="button"
                                        onClick={handleUpdate}
                                        className="px-4 py-2 bg-purple-600 text-white rounded-md shadow hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        disabled={isSubmitting} // Disable button when submitting
                                    >
                                        {isSubmitting ? 'Updating...' : 'Update'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />
            </div>
            <WhyLuminar/>
            <AboutContent />
            <CeoMessage/>
            <MissionVision/>
        </>
    )
}

export default AboutUs